<template>
    <component
        :is="element"
        :type="shadowType"
        :value="value"
        :href="href"
        :to="to"
        :disabled="props.loading || props.disabled"
        class="max-w-[calc(100vw-92px)]"
        :class="{
            'opacity-50 cursor-not-allowed': (hasStyle && props.loading) || props.disabled,
            'cursor-pointer': hasStyle && !props.loading && !props.disabled && type === 'submit',
            'py-1 px-4': hasStyle && props.size === InputSize.Small && !props.isIcon,
            'py-3 px-5': hasStyle && props.size === InputSize.Medium && !props.isIcon,
            'py-4 px-4': hasStyle && props.size === InputSize.Large && !props.isIcon,
            'rounded-full': hasStyle && !props.isIcon && props.rounded && !props.squared,
            'rounded-md': hasStyle && !props.isIcon && !props.rounded && !props.squared,
            'font-bold': props.bold,
            'font-semibold': props.semibold,
            'text-base': props.size === InputSize.Small,
            'text-lg': props.size === InputSize.Large,

            // Default
            'fill-white': hasStyle && props.variant === ButtonVariant.Default && !props.isIcon,
            'hover:border-[#b5b5b5] active:border-[#4a4a4a]': hasStyle && props.variant === ButtonVariant.Default && !props.isIcon && !disabled,
            'bg-transparent text-gray-600 fill-gray-600 border border-solid border-gray-600 hover:bg-gray-50':
                hasStyle && props.variant === ButtonVariant.Default && props.hover,
            'bg-white text-gray-600': hasStyle && props.variant === ButtonVariant.Default && !props.hover && !props.isIcon,

            // Dark
            'fill-black bg-black': hasStyle && props.variant === ButtonVariant.Dark && !props.isIcon && !props.hover,
            'hover:bg-slate-900 hover:fill-f-white': hasStyle && props.variant === ButtonVariant.Dark && !props.isIcon && !disabled,
            'bg-transparent text-black fill-black border border-solid border-black hover:bg-black hover:text-f-white hover:fill-f-white':
                hasStyle && props.variant === ButtonVariant.Dark && props.hover && !props.isIcon,
            'bg-black text-f-white': hasStyle && props.variant === ButtonVariant.Dark && !props.hover && !props.isIcon,

            // Danger
            'fill-f-red bg-f-red': hasStyle && props.variant === ButtonVariant.Danger && !props.isIcon && !props.hover,
            'hover:bg-red-500 hover:fill-f-white': hasStyle && props.variant === ButtonVariant.Danger && !props.isIcon && !disabled,
            'bg-transparent text-f-red fill-f-red border border-solid border-f-red hover:bg-f-red hover:text-f-white hover:fill-f-white':
                hasStyle && props.variant === ButtonVariant.Danger && props.hover && !props.isIcon,
            'bg-f-red text-f-white': hasStyle && props.variant === ButtonVariant.Danger && !props.hover && !props.isIcon,

            // Success
            'fill-f-green bg-f-green': hasStyle && props.variant === ButtonVariant.Success && !props.isIcon && !props.hover,
            'hover:bg-green-500 hover:fill-f-white': hasStyle && props.variant === ButtonVariant.Success && !props.isIcon && !disabled,
            'bg-transparent text-f-green fill-f-green border border-solid border-f-green hover:bg-f-green hover:text-f-white hover:fill-f-white':
                hasStyle && props.variant === ButtonVariant.Success && props.hover && !props.isIcon,
            'bg-f-green text-f-white': hasStyle && props.variant === ButtonVariant.Success && !props.hover && !props.isIcon,

            // Primary
            'fill-(--tenancy-primary-color) bg-(--tenancy-primary-color) text-white':
                hasStyle && props.variant === ButtonVariant.Primary && !props.isIcon && !props.hover,
            'hover:text-white': hasStyle && props.variant === ButtonVariant.Primary && !props.isIcon && !disabled,
            'bg-transparent text-(--tenancy-primary-color) fill-(--tenancy-primary-color) border border-solid border-(--tenancy-primary-color) hover:bg-(--tenancy-primary-color) hover:text-white hover:fill-white':
                hasStyle && props.variant === ButtonVariant.Primary && props.hover && !props.isIcon,
            'bg-(--tenancy-primary-color) text-f-white': hasStyle && props.variant === ButtonVariant.Primary && !props.hover && !props.isIcon,

            // Warning
            'fill-f-orange bg-f-orange': hasStyle && props.variant === ButtonVariant.Warning && !props.isIcon && !props.hover,
            'hover:bg-orange-500 hover:fill-f-white': hasStyle && props.variant === ButtonVariant.Warning && !props.isIcon && !disabled,
            'bg-transparent text-f-orange fill-f-orange border border-solid border-f-orange hover:bg-f-orange hover:text-f-white hover:fill-f-white':
                hasStyle && props.variant === ButtonVariant.Warning && props.hover,
            'bg-f-orange text-f-white': hasStyle && props.variant === ButtonVariant.Warning && !props.hover && !props.isIcon,

            // Tenancy
            'border-(--tenancy-primary-color) fill-(--tenancy-primary-color) bg-(--tenancy-primary-color) text-white':
                hasStyle && props.variant === ButtonVariant.Tenancy && !props.isIcon && !props.hover,
            'bg-transparent text-(--tenancy-primary-color) border-solid border-(--tenancy-primary-color) hover:bg-(--tenancy-primary-color) hover:text-f-white hover:fill-f-white':
                hasStyle && props.variant === ButtonVariant.Tenancy && props.hover && !props.isIcon,
            'bg-(--tenancy-primary-color) text-(--tenancy-primary-contrast-color)':
                hasStyle && props.variant === ButtonVariant.Tenancy && !props.hover && !props.isIcon,

            // Classes
            [props.sizeClass]: true,
        }"
        @click="onClick"
    >
        <div
            v-if="props.type !== 'submit'"
            class="inline-flex gap-3 justify-center w-full h-full"
            :class="{
                'items-baseline': props.alignContent === ContentAlignment.Baseline,
                'items-center': props.alignContent === ContentAlignment.Center,
                'text-[#128CF4]': props.type === ButtonType.Link || props.type === ButtonType.Router,
            }"
        >
            <template v-if="($slots.default || props.label) && (!props.icon || props.iconPosition === 'right')">
                <slot v-if="$slots.default" />
                <p
                    v-else
                    class="truncate"
                    :class="[props.labelClass]"
                >
                    {{ props.label }}
                </p>
            </template>
            <UiIcon
                v-if="props.icon"
                :size="localIconSize"
                :icon="props.icon"
                :hover="props.hover"
                :color="localIconColor"
                :class="{
                    [props.iconClass]: true,
                }"
                class="self-center"
            />
            <template v-if="($slots.default || props.label) && props.icon && props.iconPosition === 'left'">
                <slot v-if="$slots.default" />
                <p
                    v-else
                    class="truncate"
                    :class="[props.labelClass]"
                >
                    {{ props.label }}
                </p>
            </template>

            <input
                v-if="props.type === 'file'"
                ref="fileInput"
                :multiple="props.multiple"
                type="file"
                :accept="props.accept"
                class="hidden"
                @change="onFileChange"
            />
        </div>
    </component>
</template>

<script lang="ts" setup>
    import { InputSize, ButtonVariant, ButtonType, ContentAlignment } from '@/enums/ui.enum';
    import UiIcon from '../UiIcon/UiIcon.vue';

    const props = withDefaults(
        defineProps<{
            /** Accepted file types for file input */
            accept?: string;

            alignContent?: `${ContentAlignment}`;
            bold?: boolean;
            disabled?: boolean;
            disableStyle?: boolean;
            hover?: boolean;
            icon?: string;
            iconPosition?: 'left' | 'right';
            iconSize?: number;
            isIcon?: boolean;
            label?: string;
            loading?: boolean;

            /** Accepted file types for file input */
            multiple?: boolean;

            rounded?: boolean;
            semibold?: boolean;
            size?: `${InputSize}`;
            squared?: boolean;

            /** Button type */
            type?: `${ButtonType}`;

            /** URL for link, router or anchor buttons */
            url?: any;

            /** Button variant (tenancy, danger, success, etc )*/
            variant?: `${ButtonVariant}`;

            /**
             * Classes
             *
             */

            iconClass?: string;
            sizeClass?: string;
            labelClass?: string;
        }>(),
        {
            accept: '',
            alignContent: ContentAlignment.Baseline,
            bold: true,
            disabled: false,
            disableStyle: false,
            hover: false,
            iconPosition: 'left',
            isIcon: false,
            label: '',
            loading: false,
            rounded: true,
            semibold: false,
            size: InputSize.Medium,
            squared: false,
            type: ButtonType.Button,
            url: '',
            variant: ButtonVariant.Primary,

            /* Classes */

            iconClass: '',
            sizeClass: '',
            labelClass: '',
        },
    );

    const emit = defineEmits<{ upload: [FileList] }>();

    const fileInput = useTemplateRef<HTMLInputElement>('fileInput');

    const localIconSize = computed<number>(() => {
        switch (props.size) {
            case InputSize.Small:
                return props.isIcon ? (props.iconSize ?? 18) : (props.iconSize ?? 16);
            case InputSize.Large:
                return props.isIcon ? (props.iconSize ?? 28) : (props.iconSize ?? 24);
            case InputSize.Medium:
            default:
                return props.isIcon ? (props.iconSize ?? 24) : (props.iconSize ?? 20);
        }
    });

    const element = computed<string>(() => {
        if (props.url) {
            return 'a';
        }

        switch (props.type) {
            case ButtonType.Submit:
                return 'input';
            case ButtonType.Link:
                return 'a';
            case ButtonType.Router:
                return 'router-link';
            case ButtonType.File:
            default:
                return 'button';
        }
    });

    const hasStyle = computed<boolean>(() => {
        return ![ButtonType.Router].includes(props.type as ButtonType) && !props.disableStyle;
    });

    const href = computed<string | undefined>(() => {
        return props.type === ButtonType.Link || props.url ? props.url : undefined;
    });

    const to = computed<string | undefined>(() => {
        return props.type === ButtonType.Router ? props.url : undefined;
    });

    const shadowType = computed<string | undefined>(() => {
        switch (props.type) {
            case ButtonType.Link:
            case ButtonType.Router:
                return undefined;
            default:
                return props.url ? undefined : props.type;
        }
    });

    const value = computed<string | undefined>(() => {
        return props.type === ButtonType.Submit ? (props.label as string) : undefined;
    });

    const localIconColor = computed<string>(() => {
        if (!props.isIcon) {
            return '';
        }

        switch (props.variant) {
            case ButtonVariant.Danger:
                return 'red';
            case ButtonVariant.Success:
                return 'green';
            default:
                return '';
        }
    });

    const onFileChange = (e: Event) => {
        e.stopPropagation();

        const element: HTMLInputElement = e.target as HTMLInputElement;
        const files: FileList | null = element.files;

        if (!files || !files.length) {
            return;
        }

        emit('upload', files);
    };

    const onClick = (): void => {
        switch (props.type) {
            case ButtonType.File:
                return fileInput.value?.click();
        }
    };
</script>
